<template>
  <v-container fluid class="ai-dash">
    <!-- Seletor de empresa (ADMIN) -->
    <v-row v-if="user.tipo == 'ADMIN'" dense>
      <v-col cols="12" sm="8" md="5" lg="4">
        <v-select
          outlined
          dense
          hide-details
          background-color="white"
          prepend-inner-icon="mdi-domain"
          v-model="empresaSelecionada"
          :items="empresas"
          item-text="nome"
          item-value="id"
          label="Empresa"
          @change="carregaDashboard(empresaSelecionada)"
        ></v-select>
      </v-col>
    </v-row>

    <!-- KPIs -->
    <v-row class="mt-1">
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="12" sm="6" md="3">
        <v-card class="ai-kpi" elevation="0" rounded="lg">
          <div class="ai-kpi__icon" :style="{ background: kpi.bg, color: kpi.color }">
            <v-icon :color="kpi.color">{{ kpi.icon }}</v-icon>
          </div>
          <div class="ai-kpi__body">
            <div class="ai-kpi__value">{{ kpi.value }}</div>
            <div class="ai-kpi__label">{{ kpi.label }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráficos -->
    <v-row>
      <v-col cols="12" md="7">
        <v-card class="ai-card" elevation="0" rounded="lg">
          <div class="ai-card__head">
            <div>
              <div class="ai-card__title">Chamados por dia</div>
              <div class="ai-card__sub">Últimos 14 dias</div>
            </div>
            <v-icon color="primary">mdi-chart-areaspline</v-icon>
          </div>
          <apexchart type="area" height="300" :options="areaOptions" :series="areaSeries"></apexchart>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="ai-card" elevation="0" rounded="lg">
          <div class="ai-card__head">
            <div>
              <div class="ai-card__title">Chamadores mais acionados</div>
              <div class="ai-card__sub">Top do período</div>
            </div>
            <v-icon color="accent">mdi-podium</v-icon>
          </div>
          <apexchart
            v-if="topChamadores.labels.length"
            type="bar"
            height="300"
            :options="barOptions"
            :series="barSeries"
          ></apexchart>
          <div v-else class="ai-empty">
            <v-icon size="48" color="grey lighten-1">mdi-bell-off-outline</v-icon>
            <span>Sem chamados no período</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Últimos chamados -->
    <v-row>
      <v-col cols="12">
        <v-card class="ai-card" elevation="0" rounded="lg">
          <div class="ai-card__head">
            <div>
              <div class="ai-card__title">Últimos chamados</div>
              <div class="ai-card__sub">Registros mais recentes</div>
            </div>
            <v-icon color="primary">mdi-history</v-icon>
          </div>
          <v-simple-table v-if="ultimos.length" class="ai-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Chamador</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Tempo</th>
                  <th>Finalizado por</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in ultimos" :key="i">
                  <td>
                    <v-icon small color="primary" class="mr-1">mdi-bell-ring</v-icon>
                    {{ c.chamadorNome || '—' }}
                  </td>
                  <td>{{ formatDateHour(c.inicio) }}</td>
                  <td>{{ c.fim ? formatDateHour(c.fim) : '—' }}</td>
                  <td>
                    <v-chip x-small label :color="c.fim ? 'primary' : 'warning'" text-color="white">
                      {{ c.fim ? calculaTempo(c.inicio, c.fim) : 'em aberto' }}
                    </v-chip>
                  </td>
                  <td>{{ c.usuarioNome || '—' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-else class="ai-empty">
            <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-clock-outline</v-icon>
            <span>Nenhum chamado registrado no período</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <loading :active.sync="isLoading" :can-cancel="false" color="#038b8b" :is-full-page="fullPage"></loading>
    <Snackbar ref="chamaSnackbar" />
  </v-container>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { baseApiUrl } from '@/global'
import axios from 'axios'
import { mapState } from 'vuex'
import moment from 'moment'
import Snackbar from '../componentes/snackbar.vue'

export default {
  components: { Snackbar, Loading },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      empresas: [],
      empresaSelecionada: '',
      qtdChamadores: 0,
      qtdPaineis: 0,
      chamados: [],
    }
  },
  computed: {
    ...mapState({
      user: state => state.userObj,
      companyobj: state => state.companyObj,
    }),

    chamadosHoje() {
      const hoje = moment().format('YYYY-MM-DD')
      return this.chamados.filter(c => c.inicio && moment(c.inicio).format('YYYY-MM-DD') === hoje).length
    },

    tempoMedio() {
      const comFim = this.chamados.filter(c => c.inicio && c.fim)
      if (!comFim.length) return '—'
      const totalMs = comFim.reduce((acc, c) => acc + moment(c.fim).diff(moment(c.inicio)), 0)
      const d = moment.duration(totalMs / comFim.length)
      const m = Math.floor(d.asMinutes())
      const s = d.seconds()
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },

    kpis() {
      return [
        { label: 'Chamadores', value: this.qtdChamadores, icon: 'mdi-room-service', color: '#038b8b', bg: 'rgba(3,139,139,0.12)' },
        { label: 'Painéis', value: this.qtdPaineis, icon: 'mdi-television-classic', color: '#2B3674', bg: 'rgba(43,54,116,0.12)' },
        { label: 'Chamados hoje', value: this.chamadosHoje, icon: 'mdi-bell-ring', color: '#e09f00', bg: 'rgba(245,158,11,0.14)' },
        { label: 'Tempo médio', value: this.tempoMedio, icon: 'mdi-timer-outline', color: '#2e9b5b', bg: 'rgba(46,155,91,0.14)' },
      ]
    },

    serieDias() {
      const labels = []
      const valores = []
      for (let i = 13; i >= 0; i--) {
        const dia = moment().subtract(i, 'days')
        labels.push(dia.format('DD/MM'))
        valores.push(this.chamados.filter(c => c.inicio && moment(c.inicio).isSame(dia, 'day')).length)
      }
      return { labels, valores }
    },

    topChamadores() {
      const mapa = {}
      this.chamados.forEach(c => {
        const nome = c.chamadorNome || 'Sem nome'
        mapa[nome] = (mapa[nome] || 0) + 1
      })
      const ordenado = Object.keys(mapa)
        .map(nome => ({ nome, total: mapa[nome] }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 7)
      return {
        labels: ordenado.map(o => o.nome),
        values: ordenado.map(o => o.total),
      }
    },

    ultimos() {
      return [...this.chamados]
        .filter(c => c.inicio)
        .sort((a, b) => moment(b.inicio).diff(moment(a.inicio)))
        .slice(0, 6)
    },

    areaSeries() {
      return [{ name: 'Chamados', data: this.serieDias.valores }]
    },
    areaOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Roboto, sans-serif', sparkline: { enabled: false } },
        colors: ['#038b8b'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.03, stops: [0, 90, 100] },
        },
        xaxis: {
          categories: this.serieDias.labels,
          labels: { style: { colors: '#8a93a6' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#8a93a6' } } },
        grid: { borderColor: '#eef1f6', strokeDashArray: 4 },
        tooltip: { theme: 'light' },
      }
    },

    barSeries() {
      return [{ name: 'Chamados', data: this.topChamadores.values }]
    },
    barOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Roboto, sans-serif' },
        colors: ['#2B3674'],
        plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '62%', distributed: false } },
        dataLabels: { enabled: true, style: { colors: ['#fff'] } },
        xaxis: {
          categories: this.topChamadores.labels,
          labels: { style: { colors: '#8a93a6' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#5b6478' } } },
        grid: { borderColor: '#eef1f6', strokeDashArray: 4 },
        tooltip: { theme: 'light' },
      }
    },
  },

  mounted() {
    if (this.user.tipo == 'ADMIN') {
      this.carregaEmpresas()
    } else if (this.companyobj && this.companyobj.id) {
      this.empresaSelecionada = this.companyobj.id
      this.carregaDashboard(this.companyobj.id)
    }
  },

  methods: {
    async carregaEmpresas() {
      try {
        const res = await axios.get(baseApiUrl + 'Empresas?filter={"order":"nome ASC", "fields":{"nome": true, "id": true}}&access_token=' + sessionStorage.getItem('accToken'))
        if (res.data && res.data.length) {
          this.empresas = res.data
          this.empresaSelecionada = res.data[0].id
          this.carregaDashboard(this.empresaSelecionada)
        }
      } catch (e) {
        this.$refs.chamaSnackbar.snack('#e53935', 'Não foi possível carregar as empresas.', 'ERRO')
      }
    },

    async carregaDashboard(id) {
      if (!id) return
      this.isLoading = true
      try {
        const token = sessionStorage.getItem('accToken')
        const inicio = moment().subtract(13, 'days').startOf('day').toISOString()
        const fim = moment().endOf('day').toISOString()

        const [resChamadores, resPaineis, resChamados] = await Promise.all([
          axios.get(baseApiUrl + 'Chamadores/count?filter={"empresaId":"' + id + '"}'),
          axios.get(baseApiUrl + 'Paineis/count?filter={"empresaId":"' + id + '"}'),
          axios.get(baseApiUrl + 'Chamados?filter={"where":{"and":[{"empresaId":"' + id + '"},{"inicio":{"gt":"' + inicio + '"}},{"inicio":{"lt":"' + fim + '"}}]},"order":"inicio DESC"}&access_token=' + token),
        ])

        this.qtdChamadores = (resChamadores.data && resChamadores.data.count) || 0
        this.qtdPaineis = (resPaineis.data && resPaineis.data.count) || 0
        this.chamados = Array.isArray(resChamados.data) ? resChamados.data : []
      } catch (e) {
        this.$refs.chamaSnackbar.snack('#e53935', 'Não foi possível carregar o dashboard.', 'ERRO')
      } finally {
        this.isLoading = false
      }
    },

    formatDateHour(data) {
      if (!data) return '—'
      return moment(data).format('DD/MM/YYYY [às] HH:mm')
    },

    calculaTempo(inicio, fim) {
      if (!inicio || !fim) return '—'
      const d = moment.duration(moment(fim).diff(moment(inicio)))
      const h = Math.floor(d.asHours())
      const m = d.minutes()
      const s = d.seconds()
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
  },
}
</script>

<style scoped>
.ai-dash {
  padding: 20px 24px 32px;
  max-width: 1400px;
}

/* KPI cards */
.ai-kpi {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #eef1f6;
}
.ai-kpi__icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ai-kpi__value {
  font-size: 1.7rem;
  font-weight: 800;
  color: #0f0f41;
  line-height: 1.1;
}
.ai-kpi__label {
  font-size: 0.82rem;
  color: #8a93a6;
  font-weight: 600;
}

/* Cards */
.ai-card {
  background: #fff;
  border: 1px solid #eef1f6;
  padding: 18px 20px 8px;
  height: 100%;
}
.ai-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
}
.ai-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f0f41;
}
.ai-card__sub {
  font-size: 0.78rem;
  color: #8a93a6;
}

/* Tabela */
.ai-table >>> thead th {
  background: #f7f9fc !important;
  color: #5b6478 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ai-table >>> tbody td {
  font-size: 0.9rem;
  color: #2c3344;
}

/* Empty state */
.ai-empty {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #a7afbf;
  font-size: 0.92rem;
}
</style>
